import { useForm } from "react-hook-form";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from "~/common/mui";

interface ConfigurationNameFormData {
  name: string;
}

interface ConfigurationNameDialogProps {
  open: boolean;
  title: string;
  label: string;
  requiredMessage: string;
  mandatoryText: string;
  cancelLabel: string;
  submitLabel: string;
  submitError: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}

export const ConfigurationNameDialog = ({
  open,
  title,
  label,
  requiredMessage,
  mandatoryText,
  cancelLabel,
  submitLabel,
  submitError,
  onClose,
  onSubmit,
}: ConfigurationNameDialogProps) => {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ConfigurationNameFormData>({ defaultValues: { name: "" } });

  const submit = async ({ name }: ConfigurationNameFormData) => {
    try {
      await onSubmit(name);
      reset();
      onClose();
    } catch (error) {
      console.error(error);
      setError("root", { message: submitError });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="desktop-s"
      fullWidth
      slotProps={{ paper: { component: "form", onSubmit: handleSubmit(submit) } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ overflow: "unset" }}>
        <TextField
          {...register("name", { required: requiredMessage })}
          fullWidth
          label={label}
          error={!!errors.name}
          helperText={errors.name?.message}
        />
        {errors.root && <Alert severity="error">{errors.root.message}</Alert>}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <p>{mandatoryText}</p>
        <Stack gap={1} direction="row">
          <Button variant="text" onClick={onClose} disabled={isSubmitting}>
            {cancelLabel}
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {submitLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
